import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { SkuTagModel } from '@common/sku-tag/sku-tag.model';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Product_SkuColor, Product_SkuSize } from '@proto/store/api/store/product';
import { Skus } from '@helper/skus';
import { MenuModule } from '@module/menu-module';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { Toast } from '@helper/toast';

export class TagSku {
  constructor(
    public skuId: string,
    public color: Product_SkuColor,
    public size: Product_SkuSize,
    public inventory: number
  ) {}
}

@Component({
  selector: 'app-sku-tag',
  templateUrl: './sku-tag.component.html',
  styleUrls: ['./sku-tag.component.scss'],
  imports: [
    NgForOf,
    ImageLoadingComponent,
    MenuModule,
    MatProgressSpinnerModule,
    NgIf,
    FocusRemoverDirective,
    MatSnackBarModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SkuTagModel],
})
export class SkuTagComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Output() readonly skuChanged = new EventEmitter<TagSku>();

  @Input({ required: true }) productId = '';
  @Input({ required: true }) colorName = '';
  @Input({ required: true }) sizeName = '';
  @Input({ required: true }) suffix = '';
  @Input() disabled = false;

  constructor(
    public viewModel: SkuTagModel,
    private toast: MatSnackBar
  ) {}

  ngOnChanges(): void {
    this.viewModel.selectedColor.set(this.colorName);
    this.viewModel.selectedSize.set(this.sizeName);
  }

  onColorChange(color: Product_SkuColor, event: Event): void {
    event.stopPropagation();
    this.viewModel.selectedColor.set(color.color?.name ?? '');
  }

  onSizeChange(size: Product_SkuSize, event: Event): void {
    event.stopPropagation();
    this.viewModel.selectedSize.set(size.name);
  }

  onOpened(): void {
    this.viewModel.selectedColor.set(this.colorName);
    this.viewModel.selectedSize.set(this.sizeName);
    this.viewModel.getProduct(this.destroyRef, this.productId);
  }

  onClosed(): void {
    const color = this.viewModel.color();
    const size = this.viewModel.size();
    if (this.productId && color && size) {
      const skuId = Skus.skuId(this.productId, color.id, size.id);
      if (color.color?.name !== this.colorName || size.name !== this.sizeName) {
        Toast.open(this.toast, $localize`修改購物車成功`);
        this.skuChanged.emit(
          new TagSku(
            skuId,
            Product_SkuColor.fromPartial(color),
            Product_SkuSize.fromPartial(size),
            this.viewModel.apiResult().inventory[skuId]?.num ?? 0
          )
        );
      }
    }
  }

  trackByColor(_: number, color: Product_SkuColor): string {
    return color.id;
  }

  trackBySize(_: number, size: Product_SkuSize): string {
    return size.id;
  }
}
